'use client';
import React, { useEffect, useRef, useState, useCallback } from 'react';

// ─── CountUp Hook ────────────────────────────────────────────────────────────
function useCountUp(target: number, duration: number = 1800, started: boolean = false) {
  const [count, setCount] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!started) return;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [started, target, duration]);

  return count;
}

// ─── Individual CountUp Stat ─────────────────────────────────────────────────
interface CountStatProps {
  numericValue: number;
  prefix?: string;
  suffix?: string;
  label: string;
  sub: string;
  accent?: boolean;
  started: boolean;
  delay?: number;
  size?: 'hero' | 'large' | 'normal';
  tag?: string;
}

function CountStat({ numericValue, prefix = '', suffix = '', label, sub, accent, started, delay = 0, size = 'normal', tag }: CountStatProps) {
  const [localStarted, setLocalStarted] = useState(false);

  useEffect(() => {
    if (!started) return;
    const t = setTimeout(() => setLocalStarted(true), delay);
    return () => clearTimeout(t);
  }, [started, delay]);

  const count = useCountUp(numericValue, size === 'hero' ? 2200 : 1600, localStarted);

  const fontSize =
    size === 'hero' ? 'clamp(3.5rem, 8vw, 6rem)' :
    size === 'large' ? 'clamp(2.2rem, 4.5vw, 3.4rem)' :
    'clamp(1.6rem, 3vw, 2.4rem)';

  return (
    <div className="flex flex-col h-full">
      {tag && (
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: accent ? '#C1440E' : 'rgba(135,146,107,0.9)',
          marginBottom: '12px',
          display: 'block',
        }}>{tag}</span>
      )}
      <p
        className="font-display font-bold leading-none"
        style={{
          fontSize,
          color: accent ? '#C1440E' : 'var(--satellite-white)',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.02em',
          marginBottom: size === 'hero' ? '16px' : '10px',
        }}>
        {prefix}{count.toLocaleString()}{suffix}
      </p>
      <p style={{
        fontFamily: "'Fraunces', serif",
        fontSize: size === 'hero' ? '1rem' : '0.875rem',
        fontWeight: 500,
        color: 'rgba(242,237,224,0.9)',
        marginBottom: '6px',
        lineHeight: 1.3,
      }}>{label}</p>
      <p style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '11px',
        fontWeight: 400,
        color: 'rgba(242,237,224,0.45)',
        lineHeight: 1.5,
        fontStyle: 'italic',
      }}>{sub}</p>
    </div>
  );
}

// ─── CountStat variant for light backgrounds ─────────────────────────────────
function CountStatLight({ numericValue, prefix = '', suffix = '', label, sub, accent, started, delay = 0, size = 'normal', tag }: CountStatProps) {
  const [localStarted, setLocalStarted] = useState(false);

  useEffect(() => {
    if (!started) return;
    const t = setTimeout(() => setLocalStarted(true), delay);
    return () => clearTimeout(t);
  }, [started, delay]);

  const count = useCountUp(numericValue, size === 'hero' ? 2200 : 1600, localStarted);

  const fontSize =
    size === 'hero' ? 'clamp(3.5rem, 8vw, 6rem)' :
    size === 'large' ? 'clamp(2.2rem, 4.5vw, 3.4rem)' :
    'clamp(1.6rem, 3vw, 2.4rem)';

  return (
    <div className="flex flex-col h-full">
      {tag && (
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: accent ? '#C1440E' : 'rgba(74,107,71,0.9)',
          marginBottom: '12px',
          display: 'block',
        }}>{tag}</span>
      )}
      <p
        className="font-display font-bold leading-none"
        style={{
          fontSize,
          color: accent ? '#C1440E' : 'var(--forest)',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.02em',
          marginBottom: size === 'hero' ? '16px' : '10px',
        }}>
        {prefix}{count.toLocaleString()}{suffix}
      </p>
      <p style={{
        fontFamily: "'Fraunces', serif",
        fontSize: size === 'hero' ? '1rem' : '0.875rem',
        fontWeight: 500,
        color: 'rgba(16,46,29,0.8)',
        marginBottom: '6px',
        lineHeight: 1.3,
      }}>{label}</p>
      <p style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '11px',
        fontWeight: 400,
        color: 'rgba(16,46,29,0.45)',
        lineHeight: 1.5,
        fontStyle: 'italic',
      }}>{sub}</p>
    </div>
  );
}

// ─── Spreadsheet Row ─────────────────────────────────────────────────────────
interface SheetRowProps {
  name: string;
  description: string;
  href: string;
  index: number;
  total: number;
  tag: string;
  rowCount: string;
  visible: boolean;
}

function SheetRow({ name, description, href, index, total, tag, rowCount, visible }: SheetRowProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '24px',
        padding: '20px 24px',
        borderRadius: '12px',
        textDecoration: 'none',
        cursor: 'pointer',
        borderBottom: index < total - 1 ? '1px solid rgba(242,237,224,0.08)' : 'none',
        background: hovered ? 'rgba(135,146,107,0.12)' : 'transparent',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hovered ? '0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(135,146,107,0.2)' : 'none',
        transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: visible ? 1 : 0,
        marginLeft: visible ? '0' : '-16px',
        transitionDelay: visible ? `${index * 80}ms` : '0ms',
      }}>
      {/* Left: index + name + description */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', flex: 1, minWidth: 0 }}>
        {/* Row number */}
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '11px',
          fontWeight: 700,
          color: 'rgba(242,237,224,0.2)',
          flexShrink: 0,
          paddingTop: '3px',
          width: '20px',
          textAlign: 'right',
        }}>
          {String(index + 1).padStart(2, '0')}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            fontFamily: "'Fraunces', serif",
            fontSize: '17px',
            fontWeight: 500,
            color: hovered ? '#F2EDE0' : 'rgba(242,237,224,0.88)',
            marginBottom: '5px',
            lineHeight: 1.3,
            transition: 'color 0.2s ease',
          }}>
            {name}
          </p>
          <p style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '11px',
            fontWeight: 400,
            color: 'rgba(242,237,224,0.45)',
            lineHeight: 1.4,
            fontStyle: 'italic',
          }}>
            {description}
          </p>
        </div>
      </div>

      {/* Right: tags + arrow */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        {/* City tag */}
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: hovered ? '#87926B' : 'rgba(135,146,107,0.6)',
          padding: '4px 10px',
          borderRadius: '999px',
          border: `1px solid ${hovered ? 'rgba(135,146,107,0.5)' : 'rgba(135,146,107,0.25)'}`,
          transition: 'all 0.2s ease',
          display: 'none',
        }} className="hidden md:inline-block">
          {tag}
        </span>
        {/* Row count */}
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: 'rgba(242,237,224,0.3)',
          display: 'none',
        }} className="hidden lg:inline-block">
          {rowCount}
        </span>
        {/* Arrow */}
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '13px',
          fontWeight: 700,
          color: hovered ? '#C1440E' : 'rgba(242,237,224,0.35)',
          transition: 'all 0.2s ease',
          transform: hovered ? 'translate(3px, -3px)' : 'translate(0,0)',
          display: 'inline-block',
        }}>↗</span>
      </div>
    </a>
  );
}

// ─── Main Section ─────────────────────────────────────────────────────────────
const OutcomesSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const sheetsRef = useRef<HTMLDivElement>(null);
  const [statsStarted, setStatsStarted] = useState(false);
  const [sheetsVisible, setSheetsVisible] = useState(false);

  useEffect(() => {
    const statsObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setStatsStarted(true);
          statsObserver.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    const sheetsObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setSheetsVisible(true);
          sheetsObserver.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) statsObserver.observe(sectionRef.current);
    if (sheetsRef.current) sheetsObserver.observe(sheetsRef.current);
    return () => {
      statsObserver.disconnect();
      sheetsObserver.disconnect();
    };
  }, []);

  const sheets = [
    {
      name: 'NTBI Master Data Grid',
      description: 'Every species observed at every park, side by side',
      href: 'https://docs.google.com/spreadsheets/d/1Om8ZQ9YslTm7lmoluQ8U46BRM2hwQLmXgpTggE-wabE/edit?usp=sharing',
      tag: 'All Cities',
      rowCount: 'master · public',
    },
    {
      name: 'Overall Master Observation Table',
      description: 'Raw observations from the project — every record, unfiltered',
      href: 'https://docs.google.com/spreadsheets/d/1Fb97xIp76V5oau5rTCSYcmzTc6ogPMdfrsXDfMCyTJM/edit?usp=sharing',
      tag: 'All Cities',
      rowCount: '853 rows · public',
    },
    {
      name: 'Dallas Parks Data',
      description: 'Seven parks across Dallas — the project\'s core',
      href: 'https://docs.google.com/spreadsheets/d/1csM8OUdQDi2gVWrfNhKGO-NxSoATF4HVQoshPNwspkE/edit?usp=sharing',
      tag: 'Dallas',
      rowCount: '7 parks · public',
    },
    {
      name: 'Richardson Parks Data',
      description: 'Cottonwood Park — the riparian-corridor site',
      href: 'https://docs.google.com/spreadsheets/d/13edRY31Y-SwPAL794XLP-0YYYR-vHQKqdfiKh5keZvI/edit?usp=sharing',
      tag: 'Richardson',
      rowCount: '1 park · public',
    },
    {
      name: 'University Park Parks Data',
      description: 'Curtis Park — the ornamental-baseline site',
      href: 'https://docs.google.com/spreadsheets/d/1C8ArBpOtda9-3Dzrny5JlMZ_AUO5n34xtgAYEUtAi04/edit?usp=sharing',
      tag: 'Univ. Park',
      rowCount: '1 park · public',
    },
    {
      name: 'Irving Parks Data',
      description: 'Lively Pointe Park — added in collaboration with the City of Irving',
      href: 'https://docs.google.com/spreadsheets/d/1BvR0hR9ZxvxZK3uhh8Qqbp1oea-HoNLsPOnEi7BBLlo/edit?usp=sharing',
      tag: 'Irving',
      rowCount: '1 park · public',
    },
    {
      name: 'Azle Parks Data',
      description: 'Shady Grove Park — the western lakeside edge',
      href: 'https://docs.google.com/spreadsheets/d/1hEgHTeSERF-k3CrhEF0QxZ9vgX0xxzdFCja3FHXJVEc/edit?usp=sharing',
      tag: 'Azle',
      rowCount: '1 park · public',
    },
  ];

  return (
    <section
      id="data"
      ref={sectionRef}
      className="overflow-hidden"
      style={{ scrollMarginTop: '96px' }}>

      {/* ── YEAR 1 FINDINGS block — warm bone/parchment background ── */}
      <div className="noise-overlay py-20 md:py-28 px-6" style={{ background: 'var(--bone)', position: 'relative' }}>
        <div className="relative z-10 max-w-7xl mx-auto">

          {/* ── Section Header ─────────────────────────────────────────────── */}
          <div className="grid md:grid-cols-2 gap-8 mb-16 items-end">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-5"
                style={{ borderColor: 'rgba(16,46,29,0.2)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-saffron animate-pulse" />
                <span style={{
                  color: 'var(--forest)',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '11px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                }}>YEAR 1 FINDINGS · MAY 2026</span>
              </div>
              <h2 className="font-display font-bold leading-tight" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.4rem)', color: 'var(--forest)' }}>
                Numbers from the field,<br />
                <em className="not-italic font-light" style={{ color: 'var(--moss)' }}>not from a model.</em>
              </h2>
            </div>
            <p style={{
              fontFamily: "'Fraunces', serif",
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: 1.75,
              color: 'rgba(16,46,29,0.55)',
              maxWidth: '420px',
            }}>
              These aren&apos;t projections. Every figure comes from verified observations logged by hand across eleven parks in five cities — real species, real coordinates, real ground covered.
            </p>
          </div>

          {/* ── Editorial Stats Grid ────────────────────────────────────────── */}
          <div className="grid md:grid-cols-12 gap-4 mb-4">

            {/* HERO: 853 observations */}
            <div
              className="md:col-span-5 rounded-2xl p-8 md:p-10 flex flex-col justify-between"
              style={{
                background: 'rgba(193,68,14,0.07)',
                border: '1px solid rgba(193,68,14,0.2)',
                minHeight: '280px',
                position: 'relative',
                overflow: 'hidden',
              }}>
              <span style={{
                position: 'absolute',
                bottom: '-20px',
                right: '-10px',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 'clamp(5rem, 12vw, 9rem)',
                fontWeight: 900,
                color: 'rgba(193,68,14,0.07)',
                lineHeight: 1,
                userSelect: 'none',
                pointerEvents: 'none',
                letterSpacing: '-0.04em',
              }}>853</span>

              <div>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: 'rgba(193,68,14,0.75)',
                  display: 'block',
                  marginBottom: '20px',
                }}>PRIMARY METRIC</span>
                <CountStatLight
                  numericValue={853}
                  label="Total Observations"
                  sub="verified field records across 11 parks, 5 cities"
                  accent={true}
                  started={statsStarted}
                  delay={0}
                  size="hero"
                  tag={undefined}
                />
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '24px',
              }}>
                <span style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#C1440E',
                  display: 'inline-block',
                  flexShrink: 0,
                }} />
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  color: 'rgba(193,68,14,0.65)',
                  textTransform: 'uppercase',
                }}>iNaturalist · Research Grade</span>
              </div>
            </div>

            {/* Right column: 2×2 supporting stats */}
            <div className="md:col-span-7 grid grid-cols-2 gap-4">

              {/* Species */}
              <div className="rounded-2xl p-6 md:p-7 flex flex-col"
                style={{ background: 'rgba(16,46,29,0.05)', border: '1px solid rgba(16,46,29,0.1)' }}>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'rgba(74,107,71,0.7)',
                  marginBottom: '14px',
                  display: 'block',
                }}>BIODIVERSITY</span>
                <CountStatLight
                  numericValue={221}
                  label="Distinct Species"
                  sub="across all taxonomic groups"
                  started={statsStarted}
                  delay={150}
                  size="large"
                />
              </div>

              {/* Parks */}
              <div className="rounded-2xl p-6 md:p-7 flex flex-col"
                style={{ background: 'rgba(16,46,29,0.05)', border: '1px solid rgba(16,46,29,0.1)' }}>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'rgba(74,107,71,0.7)',
                  marginBottom: '14px',
                  display: 'block',
                }}>COVERAGE</span>
                <CountStatLight
                  numericValue={11}
                  label="Parks Surveyed"
                  sub="across 5 North Texas cities"
                  started={statsStarted}
                  delay={250}
                  size="large"
                />
              </div>

              {/* Cities */}
              <div className="rounded-2xl p-6 md:p-7 flex flex-col"
                style={{ background: 'rgba(16,46,29,0.05)', border: '1px solid rgba(16,46,29,0.1)' }}>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'rgba(74,107,71,0.7)',
                  marginBottom: '14px',
                  display: 'block',
                }}>MUNICIPALITIES</span>
                <CountStatLight
                  numericValue={5}
                  label="Cities Represented"
                  sub="Dallas, Richardson, Univ. Park, Irving, Azle"
                  started={statsStarted}
                  delay={350}
                  size="large"
                />
              </div>

              {/* Surveyors */}
              <div className="rounded-2xl p-6 md:p-7 flex flex-col"
                style={{ background: 'rgba(16,46,29,0.05)', border: '1px solid rgba(16,46,29,0.1)' }}>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'rgba(74,107,71,0.7)',
                  marginBottom: '14px',
                  display: 'block',
                }}>TEAM</span>
                <CountStatLight
                  numericValue={8}
                  label="Field Surveyors"
                  sub="student naturalists on the ground"
                  started={statsStarted}
                  delay={450}
                  size="large"
                />
              </div>
            </div>
          </div>

          {/* ── Percentage Stats Row ────────────────────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-14">
            {[
              { value: 100, suffix: '%', label: 'Geospatial Integrity', sub: 'every observation carries verified GPS coordinates', accent: false, delay: 100 },
              { value: 75, suffix: '%', label: 'Plant Dominance', sub: 'of distinct species are plants — trees, grasses, wildflowers', accent: false, delay: 200 },
              { value: 14, suffix: '%', label: 'Invasive Species Share', sub: 'of the catalog are non-native invasive species', accent: true, delay: 300 },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl p-6 md:p-7"
                style={{
                  background: s.accent ? 'rgba(193,68,14,0.06)' : 'rgba(16,46,29,0.05)',
                  border: `1px solid ${s.accent ? 'rgba(193,68,14,0.18)' : 'rgba(16,46,29,0.09)'}`,
                }}>
                <CountStatLight
                  numericValue={s.value}
                  suffix={s.suffix}
                  label={s.label}
                  sub={s.sub}
                  accent={s.accent}
                  started={statsStarted}
                  delay={s.delay}
                  size="normal"
                />
              </div>
            ))}
          </div>

          {/* ── iNaturalist CTA ─────────────────────────────────────────────── */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '32px',
          }}>
            <div style={{ flex: 1, height: '1px', background: 'rgba(16,46,29,0.12)' }} />
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'rgba(16,46,29,0.25)',
            }}>OPEN ACCESS</span>
            <div style={{ flex: 1, height: '1px', background: 'rgba(16,46,29,0.12)' }} />
          </div>

          <div
            id="mid-cta"
            className="relative rounded-3xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(16,46,29,0.06) 0%, rgba(16,46,29,0.03) 100%)',
              border: '1px solid rgba(16,46,29,0.12)',
            }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: '10%',
              right: '10%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(193,68,14,0.35), transparent)',
            }} />
            <div className="relative z-10 p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="max-w-xl">
                <p style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'rgba(193,68,14,0.75)',
                  marginBottom: '12px',
                }}>DATASET · PUBLIC</p>
                <h3 className="font-display font-bold leading-tight mb-3"
                  style={{ fontSize: 'clamp(1.4rem, 3vw, 2.2rem)', color: 'var(--forest)' }}>
                  The full dataset is open.
                </h3>
                <p style={{
                  fontFamily: "'Fraunces', serif",
                  fontSize: '0.9375rem',
                  fontWeight: 400,
                  lineHeight: 1.7,
                  color: 'rgba(16,46,29,0.55)',
                }}>
                  No paywall, no form. Browse every observation, coordinate, and photo on iNaturalist — or request a CSV export for direct use in municipal GIS systems.
                </p>
              </div>
              <a
                href="https://www.inaturalist.org/projects/north-texas-biodiversity-inventory-ntbi"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-saffron px-8 py-4 text-base font-bold whitespace-nowrap inline-flex items-center gap-3 flex-shrink-0">
                View on iNaturalist →
              </a>
            </div>
          </div>

        </div>
      </div>

      {/* ── FIELD SPREADSHEETS block — moss medium-green background ── */}
      <div className="section-green noise-overlay py-20 md:py-28 px-6">
        <div className="relative z-10 max-w-7xl mx-auto">

          {/* ── Field Spreadsheets ───────────────────────────────────────────── */}
          <div ref={sheetsRef}>
            {/* Header */}
            <div className="grid md:grid-cols-5 gap-8 md:gap-12 items-start mb-12">
              <div className="md:col-span-2">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-5"
                  style={{ borderColor: 'rgba(135,146,107,0.4)' }}>
                  <span style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    color: '#87926B',
                  }}>FIELD SPREADSHEETS · OPEN DATA</span>
                </div>
                <h2 className="section-headline-light" style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.6rem)' }}>
                  Every survey sheet,{' '}
                  <em style={{ fontStyle: 'italic', color: 'var(--canopy-light)', fontWeight: 300 }}>open to read.</em>
                </h2>
              </div>
              <div className="md:col-span-3 flex flex-col justify-center gap-5" style={{ paddingTop: '0.5rem' }}>
                <p style={{
                  fontFamily: "'Fraunces', serif",
                  fontSize: '1rem',
                  fontWeight: 400,
                  lineHeight: 1.7,
                  color: 'rgba(242,237,224,0.65)',
                }}>
                  The complete field record behind the inventory, organized by city. Anyone can open them, audit them, or download them. No login, no friction — the data is the deliverable.
                </p>
                {/* Meta stats row */}
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                  {[
                    { value: '7', label: 'spreadsheets' },
                    { value: '853', label: 'total rows' },
                    { value: '100%', label: 'public access' },
                  ].map((m) => (
                    <div key={m.label} style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                      <span style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '15px',
                        fontWeight: 700,
                        color: '#87926B',
                      }}>{m.value}</span>
                      <span style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '10px',
                        fontWeight: 400,
                        letterSpacing: '0.08em',
                        color: 'rgba(242,237,224,0.35)',
                        textTransform: 'uppercase',
                      }}>{m.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Spreadsheet list — staggered reveal */}
            <div style={{
              border: '1px solid rgba(242,237,224,0.1)',
              borderRadius: '16px',
              overflow: 'hidden',
              background: 'rgba(255,255,255,0.03)',
            }}>
              {/* Table header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 24px',
                borderBottom: '1px solid rgba(242,237,224,0.1)',
                background: 'rgba(255,255,255,0.04)',
              }}>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                  <span style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: 'rgba(242,237,224,0.25)',
                    width: '20px',
                  }}>#</span>
                  <span style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: 'rgba(242,237,224,0.25)',
                  }}>DATASET NAME</span>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: 'rgba(242,237,224,0.25)',
                  }}>SCOPE</span>
                </div>
              </div>

              {/* Rows */}
              {sheets.map((sheet, i) => (
                <SheetRow
                  key={sheet.name}
                  {...sheet}
                  index={i}
                  total={sheets.length}
                  visible={sheetsVisible}
                />
              ))}
            </div>

            {/* Footer note */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginTop: '20px',
              paddingLeft: '4px',
            }}>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '11px',
                fontWeight: 400,
                color: 'rgba(242,237,224,0.3)',
                fontStyle: 'italic',
              }}>
                All sheets hosted on Google Sheets · CC0 license · last updated May 2026
              </span>
            </div>
          </div>

        </div>
      </div>

    </section>
  );
};

export default OutcomesSection;